import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wallet, ArrowUp, ArrowUpCircle, ArrowDownCircle, RefreshCw, Loader2, X, AlertTriangle } from 'lucide-react';
import { publicApi } from '@/lib/api';

const WalletBalance = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);

  const fetchWallet = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await publicApi.get(`/wallet/${token}`);
      setData(response.data);
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.detail || 'Wallet not found');
      } else {
        setError('Unable to connect to server. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Instamojo appends payment_status (Credit or Failed) and payment_request_id on redirect
    const paymentStatus = searchParams.get('payment_status');
    const paymentRequestId = searchParams.get('payment_request_id');

    if (paymentStatus === 'Credit') {
      setPaymentMessage({ type: 'success', text: 'Payment successful! Your balance has been updated.' });
      window.history.replaceState({}, '', `/wallet/${token}`);
      // Re-fetch wallet to show updated balance
      fetchWallet();
    } else if (paymentStatus === 'Failed') {
      setPaymentMessage({ type: 'error', text: 'Payment was not completed. Please try again.' });
      window.history.replaceState({}, '', `/wallet/${token}`);
    } else if (paymentRequestId && !paymentStatus) {
      // Redirect arrived but no clear status — verify via backend
      const verifyPayment = async () => {
        try {
          const resp = await publicApi.get(`/wallet/${token}/top-up/verify/${paymentRequestId}`);
          const result = resp.data;
          if (result.status === 'completed') {
            setPaymentMessage({ type: 'success', text: 'Payment successful! Your balance has been updated.' });
            fetchWallet();
          } else if (result.status === 'failed') {
            setPaymentMessage({ type: 'error', text: 'Payment was not completed. Please try again.' });
          } else {
            setPaymentMessage({ type: 'success', text: 'Payment is being processed. Your balance will update shortly.' });
          }
        } catch {
          // Silently ignore verify errors — wallet page still loads fine
        }
        window.history.replaceState({}, '', `/wallet/${token}`);
      };
      verifyPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <Wallet className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Wallet Not Found</h2>
            <p className="text-gray-500">This wallet link may be invalid or expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm p-4">
        <div className="max-w-md mx-auto">
          <p className="text-sm text-gray-500">{data.campaign_name}</p>
          <h1 className="font-bold text-lg">{data.attendee_name}</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Payment Status Banner */}
        {paymentMessage && (
          <div className={`rounded-lg p-3 flex items-center justify-between ${
            paymentMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <p className="text-sm font-medium">{paymentMessage.text}</p>
            <button onClick={() => setPaymentMessage(null)} className="ml-2 flex-shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <CardContent className="p-6 text-center">
            <Wallet className="h-8 w-8 mx-auto mb-2 opacity-80" />
            <p className="text-blue-200 text-sm">Current Balance</p>
            <p className="text-4xl font-bold mt-1">{formatCurrency(data.balance)}</p>
            <div className="flex justify-center gap-6 mt-4 text-sm">
              <div>
                <p className="text-blue-200">Topped Up</p>
                <p className="font-semibold">{formatCurrency(data.total_topped_up)}</p>
              </div>
              <div>
                <p className="text-blue-200">Spent</p>
                <p className="font-semibold">{formatCurrency(data.total_spent)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Low Balance Warning */}
        {data.balance !== null && data.balance < 100 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Low balance!</p>
                <p className="text-sm text-amber-700 mt-0.5">
                  Top up to continue using your wallet at vendor counters.
                </p>
                <Link
                  to={`/wallet/${token}/top-up`}
                  className="text-sm font-medium text-amber-900 underline mt-1 inline-block"
                >
                  Top up now
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Link to={`/wallet/${token}/top-up`} className="flex-1">
            <Button className="w-full h-12" size="lg">
              <ArrowUp className="h-5 w-5 mr-2" /> Top Up
            </Button>
          </Link>
          <Button variant="outline" size="lg" className="h-12" onClick={fetchWallet}>
            <RefreshCw className="h-5 w-5" />
          </Button>
        </div>

        {/* Transaction History */}
        <div>
          <h3 className="font-semibold mb-3">Recent Transactions</h3>
          {data.transactions?.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-400">
                <p>No transactions yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {data.transactions?.map((tx) => {
                const isDebit = tx.type === 'purchase' || tx.type === 'debit';
                const isAdjustment = tx.type === 'adjustment';
                const iconBg = isAdjustment ? 'bg-blue-100' : isDebit ? 'bg-red-100' : 'bg-green-100';
                const iconEl = isAdjustment
                  ? <RefreshCw className="h-4 w-4 text-blue-600" />
                  : isDebit
                    ? <ArrowDownCircle className="h-4 w-4 text-red-600" />
                    : <ArrowUpCircle className="h-4 w-4 text-green-600" />;
                const amountColor = isAdjustment ? 'text-blue-600' : isDebit ? 'text-red-600' : 'text-green-600';
                const amountPrefix = isDebit ? '-' : '+';

                let label = tx.type;
                let sublabel = '';
                if (isDebit) {
                  label = tx.vendor_name || tx.items_description || 'Purchase';
                } else if (tx.type === 'top_up' || tx.type === 'credit') {
                  label = 'Top Up';
                  if (tx.payment_gateway) sublabel = `via ${tx.payment_gateway}`;
                } else if (isAdjustment) {
                  label = 'Adjustment';
                  if (tx.notes) sublabel = tx.notes;
                }

                return (
                  <Card key={tx.transaction_id}>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconBg}`}>
                          {iconEl}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{label}</p>
                          {sublabel && (
                            <p className="text-xs text-gray-500">{sublabel}</p>
                          )}
                          <p className="text-xs text-gray-400">
                            {new Date(tx.created_at).toLocaleString('en-IN', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-semibold ${amountColor}`}>
                        {amountPrefix}{formatCurrency(tx.amount)}
                      </span>
                    </CardContent>
                  </Card>
                );
              })}
              {data.transactions?.length === 20 && (
                <p className="text-center text-xs text-gray-400 pt-2">
                  Showing last 20 transactions
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletBalance;
